import {sprintf} from 'sprintf-js';
import {Cacheable, CacheStore} from '@ampeco/cache';
import {action} from 'mobx';
import {LocaleStore} from '@ampeco/i18n';
import {formatNumber} from '@ampeco/utils';
import moment from 'moment';

const BGN_CURRENCY_CODE = 'BGN';
const EUR_CURRENCY_CODE = 'EUR';
const EXCHANGE_RATE = 1.95583;
const OPERATOR_COUNTRY_BG = 'BG';
const ENABLE_DUAL_CURRENCY_DISPLAY = moment().isBefore('2026-08-09');

export class Currency implements Cacheable {
    public id: number = 0;
    public name: string = '';
    public sign: string = '';
    public code: string = '';
    public formatter: string = '';
    public unitPriceFormatter: string = '';
    public minorUnitSign?: string;
    public minorUnitPrecision?: number;
    public minorUnitDecimal: number = 0;
    public updatedAt: string = '';
    public hasPrefix: boolean = false;
    public hasSuffix: boolean = false;

    static cacheName() {
        return 'currencies';
    }

    static endpoint() {
        return '/app/currencies';
    }

    static cacheTime() {
        return 3600;
    }
    
    static useGetRequest = true;

    static fetch(ids: string | string[]) {
        CacheStore.getInstance(Currency).fetch(ids);
    }

    static where(id: string): Currency | undefined {
        return CacheStore.getInstance(Currency).cache[id];
    }

    static clearCachedData(shouldReload: boolean = false): void {
        CacheStore.getInstance(Currency).clearCachedData(shouldReload);
    }

    static build(arg: any): Currency {
        const res = new Currency();

        res.id = arg.id;
        res.name = arg.name;
        res.sign = arg.sign;
        res.code = arg.code;
        res.formatter = arg.formatter;
        res.unitPriceFormatter = arg.unitPriceFormatter;
        res.minorUnitSign = arg.minorUnitSign;
        res.minorUnitPrecision = arg.minorUnitPrecision;
        res.minorUnitDecimal = arg.minorUnitDecimal;
        res.updatedAt = arg.updated_at;
        res.hasPrefix = arg.hasPrefix;
        res.hasSuffix = arg.hasSuffix;

        return res;
    }

    static findByCode(code: string): Currency | undefined {
        const cache = CacheStore.getInstance(Currency).cache;
        return Object.values(cache).find((currency: Currency) => currency.code === code);
    }

    static findById(id: number): Currency | undefined {
        Currency.fetch(String(id));
        const cache = CacheStore.getInstance(Currency).cache;
        return Object.values(cache).find((currency: Currency) => {
            return currency.id === id;
        }) || cache[String(id)];
    }
}

const shouldShowDualCurrency = (
    currency: Currency | undefined,
    operatorCountry?: string | null
  ): boolean => {
    if (!operatorCountry || !ENABLE_DUAL_CURRENCY_DISPLAY || !currency?.code) {
      return false;
    }
    const code = currency.code;
    return (
      operatorCountry === OPERATOR_COUNTRY_BG &&
      (code === BGN_CURRENCY_CODE || code === EUR_CURRENCY_CODE)
    );
  };
  
const getSecondaryCode = (
    currency: Currency | undefined | null
  ): string | null => {
    if (!currency?.code) {
      return null;
    }
  
    if (currency.code === BGN_CURRENCY_CODE) {
      return EUR_CURRENCY_CODE;
    }
  
    if (currency.code === EUR_CURRENCY_CODE) {
      return BGN_CURRENCY_CODE;
    }
  
    return null;
  };
  
const convert = (
  amount: number,
  fromCode: string,
  toCode: string
): number => {
  if (!fromCode || !toCode) {
    return amount;
  }

  if (fromCode === toCode) {
    return amount;
  }
  if (fromCode === BGN_CURRENCY_CODE && toCode === EUR_CURRENCY_CODE) {
    return Math.round((amount / EXCHANGE_RATE) * 100) / 100;
  }

  if (fromCode === EUR_CURRENCY_CODE && toCode === BGN_CURRENCY_CODE) {
    return Math.round(amount * EXCHANGE_RATE * 100) / 100;
  }
  return amount;
};


export const supportsMinors = (currency: Currency | null): boolean => {
    return !!currency && currency.minorUnitPrecision !== undefined && !!currency.minorUnitSign;
};

export const format = (number: number, currency: Currency | null | undefined, additionalText: string = '', inMinors: boolean = false) => {
    const localizationSettings = LocaleStore.sharedInstance().localizationSettings;
    const decimals = localizationSettings.decimals;
    let formattedNumber = formatNumber(Number(number), decimals);

    if (!currency) {
        if (isNaN(Number(formattedNumber))) {
            return '-';
        }
        return formattedNumber + '';
    } else {
        formattedNumber = addCurrencySign(formattedNumber, currency);
        // @ts-ignore
        if (inMinors && supportsMinors(currency)) {
            // tslint:disable-next-line:no-non-null-assertion
            return sprintf(`%0.0${currency.minorUnitPrecision}f ${currency.minorUnitSign}`, number * Math.pow(10, currency.minorUnitDecimal!));
        } else {
            // @ts-ignore
            return formattedNumber + additionalText;
        }
    }
};

export const addCurrencySign = (formattedNumber: any, currency: Currency) => {
    let result = '';
    if (currency.hasPrefix) {
        result += currency.sign;
    }

    result += formattedNumber;

    if (currency.hasSuffix) {
        result += currency.sign;
    }

    return result;
}

export const withNoDecimals = action((number: number, currency: Currency | null | undefined, additionalText: string = '') => {
    const numberString: string = '';
    if (!currency) {
        if (isNaN(number)) {
            return '-';
        }
        return number + '';
    }

    let formatter = currency.formatter;
    if (Number.isInteger(number)) {
        formatter = currency?.formatter?.replace('0.02', '0');
    }

    return sprintf(formatter + additionalText, numberString || number);
});

type Options = {
    formatType?: DualPriceFormatTypeEnum;
    noDecimals?: boolean;
    additionalText?: string;
    inMinors?: boolean;
  };

export enum DualPriceFormatTypeEnum {
    Inline = 'inline',
    Stacked = 'stacked'
  }
  

type FormatDualPriceProps = {
    amount: number;
    currencyId: number | undefined;
    operatorCountry?: string | null;
    options?: Options;
  };

  type DualPriceProps = {
    amount: number;
    currencyId: number | undefined;
    operatorCountry?: string | null;
  };
  
  const getDualPriceInfo = 
    ({ amount, currencyId, operatorCountry }: DualPriceProps) => {
      if (!currencyId) {
        return {
          primaryCode: null,
          secondaryCode: null,
          primaryAmount: amount,
          secondaryAmount: 0,
          primaryCurrency: null,
          secondaryCurrency: null
        };
      }
      const primaryCurrency = Currency.findById(currencyId);

      const primaryCode = primaryCurrency?.code;
      const secondaryCode = getSecondaryCode(primaryCurrency);
      const shouldShowDualAmount = shouldShowDualCurrency(
        currencyId ? primaryCurrency : undefined,
        operatorCountry
      );

      if (
        !shouldShowDualAmount ||
        !primaryCode ||
        !secondaryCode ||
        !primaryCurrency
      ) {
        return {
          primaryCode,
          secondaryCode,
          primaryAmount: amount,
          secondaryAmount: 0,
          primaryCurrency,
          secondaryCurrency: undefined
        };
      }
      const primaryAmount = amount;
      const secondaryAmount = convert(amount, primaryCode, secondaryCode);
      const secondaryCurrency = Currency.findByCode(
        secondaryCode,
      );
      return {
        primaryCode,
        secondaryCode,
        primaryAmount,
        secondaryAmount,
        primaryCurrency,
        secondaryCurrency
      };
    };

  const getDualPriceOrderList = ({
      amount,
      currencyId,
      operatorCountry,
      options
    }: FormatDualPriceProps) => {
      const {
        primaryAmount,
        secondaryAmount,
        primaryCurrency,
        secondaryCurrency
      } = getDualPriceInfo({ amount, currencyId, operatorCountry });
      const formatMainCurrency = format(
          primaryAmount,
          primaryCurrency,
          options?.additionalText,
          options?.inMinors
        );

      const formatSecondaryCurrency = format(
          secondaryAmount,
          secondaryCurrency,
          options?.additionalText,
          options?.inMinors
        );

      if (!primaryCurrency || !secondaryCurrency) {
        return [
          format(
            amount,
            primaryCurrency,
            options?.additionalText,
            options?.inMinors
          )
        ];
      }
      return [formatMainCurrency, formatSecondaryCurrency];
    };
  
// Updated formatPrice function with dual currency support
export const formatDualCurrencyPrice = (
    ({
      amount,
      currencyId,
      operatorCountry,
      options
    }: FormatDualPriceProps) => {
      const optionsDefault = {
        formatType: DualPriceFormatTypeEnum.Inline,
        noDecimals: false,
        additionalText: '',
        inMinors: false,
        ...options
      };
      const dualPriceOrderList = getDualPriceOrderList({
        amount,
        currencyId,
        operatorCountry,
        options: optionsDefault
      });

      if (dualPriceOrderList.length !== 2) {
        return format(
          amount,
          Currency.findById(currencyId ?? 0),
          optionsDefault.additionalText,
          optionsDefault.inMinors
        );
      }

      switch (optionsDefault.formatType) {
        case DualPriceFormatTypeEnum.Inline:
          return `${dualPriceOrderList[0]} / ${dualPriceOrderList[1]}`;
        case DualPriceFormatTypeEnum.Stacked:
          return `${dualPriceOrderList[0]}\n${dualPriceOrderList[1]}`;
        default:
          return dualPriceOrderList[0];
      }
    }       
);

export const shouldShowDualCurrencyExchangeRate = ({
  currencyId,
  operatorCountry
}: {
  currencyId: number | undefined;
  operatorCountry?: string | null;
}) => {

  return shouldShowDualCurrency(
    Currency.findById(currencyId ?? 0),
    operatorCountry
  );
};
